<template>
  <div class="relative" v-closable="closeDropdown">
    <input
      :id="id"
      type="text"
      class="w-full border px-4 py-2 cursor-pointer"
      :value="selectedLabel"
      :disabled="disabled"
      @click="toggleDropdown"
      readonly
    />
    <span
      class="absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer"
      @click="toggleDropdown"
    >
      <arrow-icon class="w-2" />
    </span>

    <div
      v-if="isOpen"
      :class="[
        'absolute w-full z-10 rounded-b bg-white shadow',
        dropdownPosition,
      ]"
    >
      <!-- Filter Input: STAYS FIXED -->
      <div class="p-2 border-b sticky top-0 bg-white z-20">
        <input
          ref="filterInput"
          v-model="filterText"
          type="text"
          class="w-full border px-2 py-1 text-sm"
          placeholder="Escribe para buscar..."
          @keydown.tab.prevent="closeDropdown"
          @keydown.enter.prevent="selectFirstFilteredOption"
          @keydown.down.prevent="moveHighlight(1)"
          @keydown.up.prevent="moveHighlight(-1)"
        />
      </div>

      <!-- Options: SCROLLABLE -->
      <div class="max-h-72 overflow-y-auto">
        <button
          v-for="(option, index) in filteredOptions"
          :key="index"
          @click="selectOption(option)"
          :disabled="option.disabled"
          :class="[
            'text-left w-full block px-4 py-2 rounded',
            { 'text-content-300': option.value === null || option.disabled },
            {
              'cursor-pointer hover:bg-primary-300 hover:bg-opacity-10 hover:text-primary-500 smooth':
                !option.disabled,
            },
            {
              'bg-primary-300 bg-opacity-10 text-primary-500 smooth':
                option.value === modelValue,
            },
            {
              'bg-primary-500 bg-opacity-20': index === highlightedIndex,
            },
          ]"
        >
          {{ option.label }}
        </button>

        <!-- No results -->
        <div
          v-if="filteredOptions.length === 0"
          class="text-center text-content-300 py-2 text-sm"
        >
          No results found
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    id: String,
    disabled: Boolean,
    options: {
      type: Array,
      required: true,
    },
    modelValue: {
      type: [String, Number, Boolean],
      default: null,
    },
  },
  data() {
    return {
      isOpen: false,
      dropdownPosition: "top-full",
      filterText: "",
      highlightedIndex: -1,
    };
  },
  computed: {
    selectedLabel() {
      const selectedOption = this.options.find(
        (option) => option.value === this.modelValue
      );
      return selectedOption ? selectedOption.label : "";
    },
    filteredOptions() {
      if (!this.filterText) return this.options;
      return this.options.filter((option) =>
        option.label.toLowerCase().includes(this.filterText.toLowerCase())
      );
    },
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.positionDropdown();
        this.filterText = "";
        this.highlightedIndex = -1;

        this.$nextTick(() => {
          const filterInput = this.$refs.filterInput;
          if (filterInput) {
            filterInput.focus();
            filterInput.select();
          }
        });
      }
    },
    selectOption(option) {
      this.$emit("update:modelValue", option.value);
      this.$emit("change");
      this.isOpen = false;
      this.filterText = "";

      this.$nextTick(() => {
        const mainInput = this.$el.querySelector(
          'input[type="text"]:not([placeholder])'
        );
        if (mainInput) mainInput.focus();
      });
    },
    selectFirstFilteredOption() {
      const availableOptions = this.filteredOptions.filter(
        (option) => !option.disabled
      );
      if (this.highlightedIndex !== -1) {
        const option = availableOptions[this.highlightedIndex];
        if (option) {
          this.selectOption(option);
          return;
        }
      }

      if (availableOptions.length > 0) {
        this.selectOption(availableOptions[0]);
      }
    },
    moveHighlight(direction) {
      const availableOptions = this.filteredOptions.filter(
        (option) => !option.disabled
      );
      if (availableOptions.length === 0) return;

      if (this.highlightedIndex === -1) {
        this.highlightedIndex = direction > 0 ? 0 : availableOptions.length - 1;
      } else {
        this.highlightedIndex += direction;

        if (this.highlightedIndex >= availableOptions.length) {
          this.highlightedIndex = 0;
        } else if (this.highlightedIndex < 0) {
          this.highlightedIndex = availableOptions.length - 1;
        }
      }

      const newHighlightedOption = availableOptions[this.highlightedIndex];
      if (newHighlightedOption) {
        this.$emit("update:modelValue", newHighlightedOption.value);

        this.$nextTick(() => {
          const optionButtons = this.$el.querySelectorAll("button");
          if (optionButtons[this.highlightedIndex]) {
            optionButtons[this.highlightedIndex].scrollIntoView({
              block: "nearest",
            });
          }
        });
      }
    },
    closeDropdown() {
      this.isOpen = false;
    },
    positionDropdown() {
      const inputRect = this.$el.getBoundingClientRect();
      const availableSpaceBelow = window.innerHeight - inputRect.bottom;
      const availableSpaceAbove = inputRect.top;

      if (
        availableSpaceBelow < 150 &&
        availableSpaceAbove > availableSpaceBelow
      ) {
        this.dropdownPosition = "bottom-full";
      } else {
        this.dropdownPosition = "top-full";
      }
    },
  },
};
</script>
